import { calculateLoanTotal } from "@/lib/loanInterest";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate, todayISO } from "@/lib/format";
import { printCustomerProfile, printIdProof, printLoanAgreement } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";
import {
  ArrowLeft, Printer, Plus, Phone, MapPin, CreditCard, Trash2, Pencil, FileText, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings, business, currency, dateFormat } = useSettings();
  const [customer, setCustomer] = useState(null);
  const [loanOpen, setLoanOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Loan Agreement
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementLoan, setAgreementLoan] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementCreating, setAgreementCreating] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  const [agreementViewOpen, setAgreementViewOpen] = useState(false);
  // Loan disbursement payment evidence
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceLoan, setEvidenceLoan] = useState(null);
  const [evidenceList, setEvidenceList] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceSaving, setEvidenceSaving] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({
    payment_method: "PhonePe",
    payment_date: todayISO(),
    transaction_id: "",
    screenshot: "",
    notes: "",
  });

  const [saving, setSaving] = useState(false);
  const [loanForm, setLoanFormState] = useState({
    principal: "", total_repayment: "", start_date: todayISO(),
    duration_weeks: settings?.default_duration_weeks || 10,
    collection_day: "Friday",
  });
  const [editForm, setEditForm] = useState({});

  const setLoanForm = (next) => setLoanFormState((previous) => {
    const value = typeof next === "function" ? next(previous) : next;
    return { ...value, frequency: value.frequency || settings?.default_frequency || "weekly",
      total_repayment: calculateLoanTotal(value.principal, settings?.default_interest_rate ?? 30) };
  });
  useEffect(() => {
    if (!loanOpen || !settings) return;
    setLoanFormState((previous) => ({ ...previous,
      frequency: settings.default_frequency || "weekly",
      total_repayment: calculateLoanTotal(previous.principal, settings.default_interest_rate ?? 30) }));
  }, [loanOpen, settings?.default_interest_rate, settings?.default_frequency]);

  const load = useCallback(async () => {
    const r = await api.get(`/customers/${id}/profile`);
    setCustomer(r.data);
    setEditForm({
      name: r.data.name, phone: r.data.phone, address: r.data.address,
      id_type: r.data.id_type, id_number: r.data.id_number, notes: r.data.notes || "",
      photo: r.data.photo || "", id_proof: r.data.id_proof || "",
      guarantor_name: r.data.guarantor_name || "", guarantor_phone: r.data.guarantor_phone || "",
      guarantor_address: r.data.guarantor_address || "", guarantor_relation: r.data.guarantor_relation || "",
      guarantor_photo: r.data.guarantor_photo || "",
    });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openLoanAgreement = () => {
    setAgreementLoan(null);
    setAgreementOpen(true);
  };

  const createLoanAgreement = async () => {

    if (!agreementLoan) {
      toast.error("Please select a loan");
      return;
    }

    setAgreementCreating(true);

    try {
      const r = await api.post(
        `/customers/${id}/loans/${agreementLoan.id}/agreement`
      );

      toast.success(
        `Agreement ${r.data.agreement_number || ""} created successfully`
      );
        setAgreementOpen(false);
        setAgreementData(r.data);
        setAgreementViewOpen(true);
        setAgreementLoan(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAgreementCreating(false);
    }
  };

  const viewLoanAgreement = async () => {
    if (!agreementLoan) {
      toast.error("Please select a loan");
      return;
    }

    setAgreementLoading(true);

    try {
      const r = await api.get(
        `/customers/${id}/loans/${agreementLoan.id}/agreement`
      );

      if (!r.data?.exists || !r.data?.agreement) {
        toast.error("No agreement has been created for this loan yet");
        return;
      }

      setAgreementData(r.data.agreement);
      setAgreementViewOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAgreementLoading(false);
    }
  };

  const addLoan = async () => {
    const p = parseFloat(loanForm.principal), t = parseFloat(loanForm.total_repayment);
    if (!p || !t) { toast.error("Enter loan amount and total repayment"); return; }
    if (t < p) { toast.error("Total repayment should be ≥ loan amount"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${id}/loans`, {
        principal: p, total_repayment: t, start_date: loanForm.start_date,
        duration_weeks: parseInt(loanForm.duration_weeks) || 10,
        frequency: loanForm.frequency || settings?.default_frequency || "weekly",
        collection_day: loanForm.collection_day || "Friday",
      });
      toast.success("Loan created with EMI schedule");
      setLoanOpen(false);
      setLoanForm({
        principal: "",
        total_repayment: "",
        start_date: todayISO(),
        duration_weeks: settings?.default_duration_weeks || 10,
        collection_day: "Friday",
      });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };


  const openDisbursementEvidence = async (loan) => {
    setEvidenceLoan(loan);
    setEvidenceOpen(true);
    setEvidenceLoading(true);
    setEvidenceList([]);

    try {
      const r = await api.get(
        `/loans/${loan.id}/disbursement-evidence`
      );
      setEvidenceList(r.data || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setEvidenceLoading(false);
    }

    setEvidenceForm({
      payment_method: "PhonePe",
      payment_date: todayISO(),
      transaction_id: "",
      screenshot: "",
      notes: "",
    });
  };

  const saveDisbursementEvidence = async () => {
    if (!evidenceLoan) return;

    if (!evidenceForm.payment_date) {
      toast.error("Payment date is required");
      return;
    }

    if (!evidenceForm.screenshot) {
      toast.error("Payment screenshot is required");
      return;
    }

    setEvidenceSaving(true);

    try {
      const r = await api.post(
        `/loans/${evidenceLoan.id}/disbursement-evidence`,
        evidenceForm
      );

      setEvidenceList((prev) => [r.data, ...prev]);

      setEvidenceForm({
        payment_method: "PhonePe",
        payment_date: todayISO(),
        transaction_id: "",
        screenshot: "",
        notes: "",
      });

      toast.success("Payment evidence saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setEvidenceSaving(false);
    }
  };

  const deleteDisbursementEvidence = async (loan, evidenceId) => {
    if (!window.confirm("Delete this payment evidence?")) return;

    try {
      await api.delete(
        `/loans/${loan.id}/disbursement-evidence/${evidenceId}`
      );

      setEvidenceList((prev) =>
        prev.filter((item) => item.id !== evidenceId)
      );

      toast.success("Payment evidence deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/customers/${id}`, editForm);
      toast.success("Customer updated");
      setEditOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeCustomer = async () => {
    if (!window.confirm("Delete this customer and all their loans?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    navigate("/customers");
  };

  if (!customer) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Back to customers
      </button>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-navy text-white flex items-center justify-center font-heading text-2xl font-bold overflow-hidden">
              {customer.photo ? <img src={customer.photo} alt={customer.name} className="h-full w-full object-cover" /> : customer.name[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-navy">{customer.name}</h1>
              <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{customer.phone || "—"}</span>
                <span className="flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" />{customer.id_type}: {customer.id_number || "—"}</span>
              </div>
              {customer.address && <div className="mt-1 text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{customer.address}</div>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => printCustomerProfile(customer, business, settings)} data-testid="print-customer-pdf-button">
              <Printer className="h-4 w-4 mr-2" /> Print PDF
            </Button>
              <Button
                variant="outline"
                className="text-maroon border-maroon/30 hover:bg-maroon/5"
                onClick={(e) => {
                  e.stopPropagation();
                  openLoanAgreement();
                }}
                data-testid="loan-agreement-button"
              >
                <FileText className="h-4 w-4 mr-2" /> Loan Agreement
              </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-customer-button">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={removeCustomer} data-testid="delete-customer-button">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Documents & Guarantor */}
      {(customer.photo || customer.id_proof || customer.guarantor_name) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(customer.photo || customer.id_proof) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-heading font-semibold text-navy mb-3">Documents</h3>
              <div className="flex gap-4">
                {customer.photo && (
                  <div className="text-center">
                    <img src={customer.photo} alt="Customer" className="h-28 w-28 rounded-lg object-cover border border-slate-200" data-testid="customer-photo-preview" />
                    <div className="text-xs text-slate-400 mt-1">Photo</div>
                  </div>
                )}
                {customer.id_proof && (
                  <div className="text-center">
                    <img src={customer.id_proof} alt="ID Proof" className="h-28 w-44 rounded-lg object-cover border border-slate-200" data-testid="customer-idproof-preview" />
                    <div className="text-xs text-slate-400 mt-1">ID Proof</div>
                    <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => printIdProof(customer, business, settings)} data-testid="print-idproof-button">
                      <Printer className="h-3.5 w-3.5 mr-1" /> Print / Save
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          {(customer.guarantor_name || customer.guarantor_photo) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-maroon" />
                Guarantor
              </h3>

              <div className="flex flex-col sm:flex-row gap-5">
                {customer.guarantor_photo && (
                  <div className="shrink-0">
                    <img
                      src={customer.guarantor_photo}
                      alt="Guarantor"
                      className="h-28 w-28 rounded-lg object-cover border border-slate-200"
                      data-testid="guarantor-photo-preview"
                    />
                    <div className="text-xs text-slate-400 mt-1 text-center">
                      Guarantor Photo
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm flex-1">
                  <div>
                    <div className="text-xs text-slate-400">Name</div>
                    <div className="font-medium text-navy">{customer.guarantor_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Phone</div>
                    <div className="font-medium text-navy">{customer.guarantor_phone || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Relation</div>
                    <div className="font-medium text-navy">{customer.guarantor_relation || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Address</div>
                    <div className="font-medium text-navy">{customer.guarantor_address || "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loans */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-navy">Loans ({customer.loans.length})</h2>
        <Button className="bg-maroon hover:bg-maroon-hover" onClick={() => setLoanOpen(true)} data-testid="add-loan-button">
          <Plus className="h-4 w-4 mr-2" /> Add Loan
        </Button>
      </div>

      {customer.loans.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {customer.loans.map((loan) => (
            <div key={loan.id} onClick={() => navigate(`/loans/${loan.id}`)} data-testid={`loan-card-${loan.id}`}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-maroon/40 transition-all cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-maroon" />
                  <span className="font-mono font-bold text-navy">{loan.loan_number}</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${loan.status === "active" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {loan.status}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-slate-400">Amount</div><div className="font-mono font-semibold">{fmtMoney(loan.principal, currency)}</div></div>
                <div><div className="text-xs text-slate-400">{loan.frequency === "monthly" ? "Monthly EMI" : "Weekly EMI"}</div><div className="font-mono font-semibold">{fmtMoney(loan.weekly_emi, currency)}</div></div>
                <div><div className="text-xs text-slate-400">Outstanding</div><div className="font-mono font-semibold text-maroon">{fmtMoney(loan.outstanding, currency)}</div></div>
              </div>
              <div className="mt-3 text-xs text-slate-400">Started {fmtDate(loan.start_date, dateFormat)} · {loan.duration_weeks} {loan.frequency === "monthly" ? "months" : "weeks"}</div>

<div className="mt-3 flex gap-2 flex-wrap">
    <Button
      variant="outline"
      onClick={(e) => {
        e.stopPropagation();
        openDisbursementEvidence(loan);
      }}
      data-testid={`disbursement-evidence-${loan.id}`}
    >
      <CreditCard className="h-4 w-4 mr-2" />
      Payment Evidence
    </Button>

  {String(loan.lifecycle_status || "").toLowerCase() !== "renewed" && (
<Button
                  className="bg-maroon hover:bg-maroon-hover"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/loans/${loan.id}`);
      }}
      data-testid={`renew-loan-${loan.id}`}
    >
      Renew Loan
    </Button>
  )}
</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
          No loans yet. Add a loan to generate the weekly EMI schedule.
        </div>
      )}

      {/* Add loan dialog */}
      <Dialog open={loanOpen} onOpenChange={setLoanOpen}>
        <DialogContent data-testid="add-loan-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Loan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Repayment Frequency</Label><select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3" value={loanForm.frequency || settings?.default_frequency || "weekly"} onChange={(e) => setLoanForm({ ...loanForm, frequency: e.target.value })}><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></div>
            <div><Label>Loan Amount ({currency})</Label>
              <Input type="number" value={loanForm.principal} onChange={(e) => setLoanForm({ ...loanForm, principal: e.target.value })} data-testid="loan-principal-input" /></div>
            <div><Label>Total Repayment ({currency})</Label>
              <Input readOnly type="number" value={loanForm.total_repayment} onChange={(e) => setLoanForm({ ...loanForm, total_repayment: e.target.value })} data-testid="loan-total-input" /></div>
            <div><Label>Start Date (any day)</Label>
              <Input type="date" value={loanForm.start_date} onChange={(e) => setLoanForm({ ...loanForm, start_date: e.target.value })} data-testid="loan-startdate-input" /></div>
            <div><Label>Duration ({loanForm.frequency === "monthly" ? "months" : "weeks"})</Label>
              <Input type="number" value={loanForm.duration_weeks} onChange={(e) => setLoanForm({ ...loanForm, duration_weeks: e.target.value })} data-testid="loan-duration-input" /></div>
              <div><Label>Collection Day</Label>
                <Select
                  value={loanForm.collection_day}
                  onValueChange={(value) => setLoanForm({ ...loanForm, collection_day: value })}
                >
                  <SelectTrigger data-testid="loan-collection-day-select">
                    <SelectValue placeholder="Select collection day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monday">Monday</SelectItem>
                    <SelectItem value="Tuesday">Tuesday</SelectItem>
                    <SelectItem value="Wednesday">Wednesday</SelectItem>
                    <SelectItem value="Thursday">Thursday</SelectItem>
                    <SelectItem value="Friday">Friday</SelectItem>
                    <SelectItem value="Saturday">Saturday</SelectItem>
                    <SelectItem value="Sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
          </div>
          {loanForm.principal && loanForm.total_repayment && (
            <div className="text-sm bg-slate-50 rounded-lg p-3 flex justify-between">
              <span className="text-slate-500">{loanForm.frequency === "monthly" ? "Monthly EMI ≈" : "Weekly EMI ≈"}</span>
              <span className="font-mono font-bold text-navy">{fmtMoney((parseFloat(loanForm.total_repayment) / (parseInt(loanForm.duration_weeks) || 10)), currency)}</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanOpen(false)}>Cancel</Button>
            <Button className="bg-maroon hover:bg-maroon-hover" onClick={addLoan} disabled={saving} data-testid="create-loan-submit">
              {saving ? "Creating…" : "Create Loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Disbursement Payment Evidence */}
      <Dialog open={evidenceOpen} onOpenChange={setEvidenceOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Loan Disbursement Payment Evidence
            </DialogTitle>
          </DialogHeader>

          {evidenceLoan && (
            <div className="space-y-5">

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-slate-400">Loan Number</div>
                    <div className="font-mono font-semibold text-navy">
                      {evidenceLoan.loan_number}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-slate-400">Amount Given</div>
                    <div className="font-mono font-semibold text-navy">
                      {fmtMoney(
                        evidenceLoan.amount_given_to_customer ??
                        evidenceLoan.principal ??
                        0,
                        currency
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-heading font-semibold text-navy mb-3">
                  Add Payment Proof
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <div>
                    <Label>Payment Method</Label>
                    <Select
                      value={evidenceForm.payment_method}
                      onValueChange={(value) =>
                        setEvidenceForm({
                          ...evidenceForm,
                          payment_method: value
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PhonePe">PhonePe</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Bank Transfer">
                          Bank Transfer
                        </SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={evidenceForm.payment_date}
                      onChange={(e) =>
                        setEvidenceForm({
                          ...evidenceForm,
                          payment_date: e.target.value
                        })
                      }
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Transaction ID / UTR</Label>
                    <Input
                      value={evidenceForm.transaction_id}
                      onChange={(e) =>
                        setEvidenceForm({
                          ...evidenceForm,
                          transaction_id: e.target.value
                        })
                      }
                      placeholder="PhonePe / UPI transaction ID"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <ImageUpload
                      label="Payment Screenshot"
                      value={evidenceForm.screenshot}
                      onChange={(value) =>
                        setEvidenceForm({
                          ...evidenceForm,
                          screenshot: value
                        })
                      }
                      testid="disbursement-payment-screenshot"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={evidenceForm.notes}
                      onChange={(e) =>
                        setEvidenceForm({
                          ...evidenceForm,
                          notes: e.target.value
                        })
                      }
                      placeholder="Optional notes"
                    />
                  </div>

                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    className="bg-maroon hover:bg-maroon-hover"
                    onClick={saveDisbursementEvidence}
                    disabled={evidenceSaving}
                  >
                    {evidenceSaving
                      ? "Saving..."
                      : "Save Payment Evidence"}
                  </Button>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <h3 className="font-heading font-semibold text-navy mb-3">
                  Saved Payment Evidence
                </h3>

                {evidenceLoading ? (
                  <div className="text-sm text-slate-400 py-4">
                    Loading payment evidence...
                  </div>
                ) : evidenceList.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4">
                    No payment evidence saved for this loan.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {evidenceList.map((item) => (
                      <div
                        key={item.id}
                        className="border border-slate-200 rounded-lg p-4"
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">

                          <div>
                            <div className="text-xs text-slate-400">
                              Method
                            </div>
                            <div className="font-medium">
                              {item.payment_method || "—"}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-400">
                              Date
                            </div>
                            <div className="font-medium">
                              {item.payment_date || "—"}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-400">
                              Amount
                            </div>
                            <div className="font-mono font-semibold">
                              {fmtMoney(item.amount || 0, currency)}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-slate-400">
                              Transaction ID
                            </div>
                            <div className="font-mono text-xs break-all">
                              {item.transaction_id || "—"}
                            </div>
                          </div>

                        </div>

                        {item.screenshot && (
                          <div className="mt-3">
                            <img
                              src={item.screenshot}
                              alt="Payment proof"
                              className="max-h-64 max-w-full rounded-lg border border-slate-200 object-contain"
                            />
                          </div>
                        )}

                        {item.notes && (
                          <div className="mt-3 text-sm text-slate-500">
                            <span className="font-medium">Notes:</span>{" "}
                            {item.notes}
                          </div>
                        )}

                        <div className="mt-3 flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-600"
                            onClick={() =>
                              deleteDisbursementEvidence(
                                evidenceLoan,
                                item.id
                              )
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Delete
                          </Button>
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEvidenceOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      
        {/* Loan Agreement Dialog */}
        <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
          <DialogContent
            className="max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="loan-agreement-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-navy">
                Loan Agreement
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">

              {/* Parties */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  First Party — Borrower
                </div>

                <div className="mt-1 text-lg font-semibold text-navy">
                  {customer.name}
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-400">Phone</div>
                    <div className="font-medium text-navy">
                      {customer.phone || "—"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-slate-400">
                      {customer.id_type || "ID"} Number
                    </div>
                    <div className="font-medium text-navy">
                      {customer.id_number || "—"}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="text-xs text-slate-400">Address</div>
                    <div className="font-medium text-navy">
                      {customer.address || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Second Party — Lender
                </div>

                <div className="mt-1 text-lg font-semibold text-navy">
                  Akash Bhavani
                </div>

                <div className="text-sm text-slate-500">
                  FinLend
                </div>
              </div>

              {/* Loan Selection */}
              <div>
                <Label>Select Loan for Agreement</Label>

                {customer.loans?.length ? (
                  <div className="mt-3 space-y-3">
                    {customer.loans.map((loan) => {
                      const selected = agreementLoan?.id === loan.id;

                      return (
                        <button
                          key={loan.id}
                          type="button"
                          onClick={() => setAgreementLoan(loan)}
                          className={`w-full rounded-lg border p-4 text-left transition-all ${
                            selected
                              ? "border-maroon bg-maroon/5 ring-1 ring-maroon"
                              : "border-slate-200 hover:border-maroon/40 hover:bg-slate-50"
                          }`}
                          data-testid={`agreement-loan-${loan.id}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-mono font-bold text-navy">
                                {loan.loan_number}
                              </div>

                              <div className="mt-1 text-xs text-slate-400">
                                Start Date:{" "}
                                {fmtDate(loan.start_date, dateFormat)}
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="font-mono font-semibold text-navy">
                                {fmtMoney(loan.principal, currency)}
                              </div>

                              <div className="text-xs text-slate-400">
                                Loan Amount
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-slate-400">
                                Total Repayment
                              </div>
                              <div className="font-mono font-semibold text-navy">
                                {fmtMoney(
                                  loan.total_repayment,
                                  currency
                                )}
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-slate-400">
                                Weekly EMI
                              </div>
                              <div className="font-mono font-semibold text-navy">
                                {fmtMoney(
                                  loan.weekly_emi,
                                  currency
                                )}
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-slate-400">
                                Duration
                              </div>
                              <div className="font-semibold text-navy">
                                {loan.duration_weeks || "—"} weeks
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-slate-400">
                                Status
                              </div>
                              <div className="font-semibold capitalize text-navy">
                                {loan.status ||
                                  loan.lifecycle_status ||
                                  "—"}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
                    No loans available for this customer.
                  </div>
                )}
              </div>

              {/* Agreement acknowledgement */}
              <div className="rounded-lg border border-maroon/20 bg-maroon/5 p-4 text-sm text-slate-700">
                <div className="font-semibold text-navy mb-1">
                  Borrower Agreement
                </div>

                <p>
                  The borrower confirms that they have agreed to avail the
                  loan from FinLend and accept the loan amount, repayment
                  schedule, applicable charges, and the terms and conditions
                  of the loan agreement.
                </p>
              </div>

              {agreementLoading && (
                <div className="text-sm text-slate-400">
                  Loading agreement details...
                </div>
              )}

            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setAgreementOpen(false);
                  setAgreementLoan(null);
                }}
              >
                Cancel
              </Button>

                              <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    viewLoanAgreement();
                  }}
                  disabled={!agreementLoan || agreementLoading}
                  data-testid="view-loan-agreement-button"
                >
                  {agreementLoading ? "Loading..." : "View Agreement"}
                </Button>

<Button
                className="bg-maroon hover:bg-maroon-hover"
                type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    createLoanAgreement();
                  }}
                disabled={!agreementLoan || agreementCreating}
                data-testid="create-loan-agreement-button"
              >
                {agreementCreating
                  ? "Creating…"
                  : "Create Agreement"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* View Loan Agreement Dialog */}
        <Dialog
          open={agreementViewOpen}
          onOpenChange={setAgreementViewOpen}
        >
          <DialogContent
            className="max-w-4xl max-h-[92vh] overflow-y-auto"
            data-testid="view-loan-agreement-dialog"
          >
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Loan Agreement
              </DialogTitle>
            </DialogHeader>

            {agreementData && (
              <div className="space-y-5 text-sm">

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-maroon">
                        FinLend
                      </div>
                      <div className="mt-1 text-xl font-bold text-navy">
                        LOAN AGREEMENT
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Loan agreement between the Borrower and the Lender.
                      </div>
                    </div>

                    <div className="sm:text-right">
                      <div className="text-xs text-slate-400">
                        Agreement Number
                      </div>
                      <div className="font-mono font-bold text-navy">
                        {agreementData.agreement_number || "—"}
                      </div>

                      <div className="mt-1 text-xs text-slate-400">
                        Agreement Date
                      </div>
                      <div className="font-medium text-navy">
                        {agreementData.agreement_date || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div className="rounded-xl border border-slate-200 p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-maroon">
                      First Party — Borrower
                    </div>

                    <div className="mt-2 text-lg font-bold text-navy">
                      {agreementData.borrower?.name || "—"}
                    </div>

                    <div className="mt-4 space-y-2">
                      <div>
                        <span className="text-slate-400">Phone: </span>
                        <span className="font-medium">
                          {agreementData.borrower?.phone || "—"}
                        </span>
                      </div>

                      <div>
                        <span className="text-slate-400">
                          {agreementData.borrower?.id_type || "ID"} Number:{" "}
                        </span>
                        <span className="font-medium">
                          {agreementData.borrower?.id_number || "—"}
                        </span>
                      </div>

                      <div>
                        <div className="text-slate-400">Address</div>
                        <div className="font-medium text-navy">
                          {agreementData.borrower?.address || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-maroon">
                      Second Party — Lender
                    </div>

                    <div className="mt-2 text-lg font-bold text-navy">
                      {agreementData.lender_name || "Akash Bhavani"}
                    </div>

                    <div className="mt-1 font-medium text-slate-500">
                      {agreementData.lender_business_name || "FinLend"}
                    </div>
                  </div>

                </div>

                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-5 py-3 font-bold text-navy">
                    Loan Details
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">

                    <div>
                      <div className="text-xs text-slate-400">
                        Loan Number
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        {agreementData.loan_number || "—"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Loan Amount
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        ₹{Number(
                          agreementData.loan?.principal || 0
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Processing Fee
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        ₹{Number(
                          agreementData.loan?.processing_fee || 0
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Amount Given
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        ₹{Number(
                          agreementData.loan?.amount_given_to_customer || 0
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Total Repayment
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        ₹{Number(
                          agreementData.loan?.total_repayment || 0
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Weekly EMI
                      </div>
                      <div className="mt-1 font-mono font-bold">
                        ₹{Number(
                          agreementData.loan?.weekly_emi || 0
                        ).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Duration
                      </div>
                      <div className="mt-1 font-bold">
                        {agreementData.loan?.duration_weeks || "—"} weeks
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-400">
                        Collection Day
                      </div>
                      <div className="mt-1 font-bold">
                        {agreementData.loan?.collection_day || "Friday"}
                      </div>
                    </div>

                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-5 py-3 font-bold text-navy">
                    Repayment Schedule
                  </div>

                  {agreementData.repayment_schedule?.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="px-4 py-3 text-left">Week</th>
                            <th className="px-4 py-3 text-left">Due Date</th>
                            <th className="px-4 py-3 text-right">EMI</th>
                            <th className="px-4 py-3 text-left">Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {agreementData.repayment_schedule.map((row, index) => (
                            <tr
                              key={row.id || index}
                              className="border-b last:border-b-0"
                            >
                              <td className="px-4 py-3 font-medium">
                                {row.week ||
                                  row.week_number ||
                                  index + 1}
                              </td>

                              <td className="px-4 py-3">
                                {row.due_date ||
                                  row.date ||
                                  "—"}
                              </td>

                              <td className="px-4 py-3 text-right font-mono font-semibold">
                                ₹{Number(
                                  row.emi ||
                                  row.amount ||
                                  row.installment ||
                                  0
                                ).toLocaleString("en-IN")}
                              </td>

                              <td className="px-4 py-3">
                                {row.status || "Pending"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-5 text-center text-slate-400">
                      No repayment schedule available.
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 p-5">
                  <div className="font-bold text-navy">
                    Borrower Acknowledgement & Terms
                  </div>

                  <div className="mt-3 space-y-2 leading-6 text-slate-600">
                    <p>
                      The Borrower confirms that the loan details stated in
                      this agreement have been reviewed and accepted.
                    </p>

                    <p>
                      The Borrower agrees to repay the total repayment amount
                      according to the repayment schedule stated above and
                      acknowledges the applicable processing fee and other
                      agreed charges.
                    </p>

                    <p>
                      The Borrower confirms that the information supplied to
                      FinLend is true and correct and agrees to comply with
                      the agreed repayment schedule and collection
                      arrangements.
                    </p>

                    <p>
                      This agreement records the understanding between the
                      Borrower and the Lender in respect of the above loan.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-5">
                  <div>
                    <div className="border-b border-slate-300 h-14"></div>
                    <div className="mt-2 font-semibold text-navy">
                      Borrower Signature
                    </div>
                    <div className="text-xs text-slate-500">
                      {agreementData.borrower?.name || "Borrower"}
                    </div>
                  </div>

                  <div>
                    <div className="border-b border-slate-300 h-14"></div>
                    <div className="mt-2 font-semibold text-navy">
                      Lender Signature
                    </div>
                    <div className="text-xs text-slate-500">
                      {agreementData.lender_name || "Akash Bhavani"}
                    </div>
                  </div>
                </div>

              </div>
            )}

            <DialogFooter>                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    printLoanAgreement(
                      agreementData,
                      customer,
                      business,
                      settings
                    );
                  }}
                  disabled={!agreementData}
                  data-testid="print-loan-agreement-button"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print / Save PDF
                </Button>


              <Button
                type="button"
                variant="outline"
                onClick={() => setAgreementViewOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Full Name</Label>
              <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><Label>Phone</Label>
              <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div><Label>ID Type</Label>
              <Input value={editForm.id_type || ""} onChange={(e) => setEditForm({ ...editForm, id_type: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>ID Number</Label>
              <Input value={editForm.id_number || ""} onChange={(e) => setEditForm({ ...editForm, id_number: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Address</Label>
              <Textarea value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>

            <div><ImageUpload label="Customer Photo (optional)" value={editForm.photo} onChange={(v) => setEditForm({ ...editForm, photo: v })} testid="edit-customer-photo-upload" /></div>
            <div><ImageUpload label="ID Proof Photo" value={editForm.id_proof} onChange={(v) => setEditForm({ ...editForm, id_proof: v })} testid="edit-customer-idproof-upload" /></div>

            <div className="sm:col-span-2 pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-navy font-heading">Guarantor Details</p>
            </div>
            <div><Label>Guarantor Name</Label>
              <Input value={editForm.guarantor_name || ""} onChange={(e) => setEditForm({ ...editForm, guarantor_name: e.target.value })} data-testid="edit-guarantor-name-input" /></div>
            <div><Label>Guarantor Phone</Label>
              <Input value={editForm.guarantor_phone || ""} onChange={(e) => setEditForm({ ...editForm, guarantor_phone: e.target.value })} data-testid="edit-guarantor-phone-input" /></div>
            <div><Label>Relation to Customer</Label>
              <Input value={editForm.guarantor_relation || ""} onChange={(e) => setEditForm({ ...editForm, guarantor_relation: e.target.value })} data-testid="edit-guarantor-relation-input" /></div>
            <div><Label>Guarantor Address</Label>
              <Input value={editForm.guarantor_address || ""} onChange={(e) => setEditForm({ ...editForm, guarantor_address: e.target.value })} data-testid="edit-guarantor-address-input" /></div>
            <div className="sm:col-span-2">
              <ImageUpload
                label="Guarantor Photo (optional)"
                value={editForm.guarantor_photo || ""}
                onChange={(v) => setEditForm({ ...editForm, guarantor_photo: v })}
                testid="edit-guarantor-photo-upload"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-maroon hover:bg-maroon-hover" onClick={saveEdit} disabled={saving} data-testid="save-edit-customer-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
